import { Box, Button } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  APP_COLOR,
  APP_HOVER_COLOR,
  BUTTON_TEXT_COLOR,
  FONT_FAMILY,
  TEXT_COLOR,
} from "../../theme";

export type GuestBannerState = "fresh" | "warn" | "expire";

export interface GuestBannerProps {
  state?: GuestBannerState;
  daysLabel?: string;
  usedMB: number;
  totalMB?: number;
  onSave?: () => void;
  saveLabel?: string;
}

const AMBER = "#E3A549";
const AMBER_INK = "#2a1d07";
const RED = "#E5575C";

const palette = (state: GuestBannerState) => {
  if (state === "warn") {
    return {
      bg: "rgba(227,165,73,0.13)",
      border: "rgba(227,165,73,0.5)",
      icBg: "rgba(227,165,73,0.2)",
      ic: AMBER,
      barFill: AMBER,
      ctaBg: AMBER,
      ctaText: AMBER_INK,
      ctaHover: AMBER,
    };
  }
  if (state === "expire") {
    return {
      bg: "rgba(229,87,92,0.13)",
      border: "rgba(229,87,92,0.5)",
      icBg: "rgba(229,87,92,0.2)",
      ic: RED,
      barFill: RED,
      ctaBg: RED,
      ctaText: "#FFFFFF",
      ctaHover: RED,
    };
  }
  return {
    bg: "rgba(39,182,203,0.12)",
    border: "rgba(39,182,203,0.42)",
    icBg: "rgba(39,182,203,0.18)",
    ic: APP_COLOR,
    barFill: APP_COLOR,
    ctaBg: APP_COLOR,
    ctaText: BUTTON_TEXT_COLOR,
    ctaHover: APP_HOVER_COLOR,
  };
};

const GuestBanner: React.FC<GuestBannerProps> = ({
  state = "fresh",
  daysLabel,
  usedMB,
  totalMB = 100,
  onSave,
  saveLabel = "Save your work",
}) => {
  const p = palette(state);
  const pct = Math.min(100, Math.max(0, Math.round((usedMB / totalMB) * 100)));
  const defaultDays =
    state === "expire" ? "Ends today" : state === "warn" ? "2 days left" : "5 days left";

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        minHeight: 44,
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "9px 22px",
        background: p.bg,
        borderBottom: `1px solid ${p.border}`,
        color: TEXT_COLOR,
        fontFamily: FONT_FAMILY,
        fontSize: 14,
        flexWrap: "wrap",
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          flex: "0 0 26px",
          borderRadius: "8px",
          display: "grid",
          placeItems: "center",
          background: p.icBg,
          color: p.ic,
        }}
      >
        {state === "fresh" ? (
          <AccessTimeIcon sx={{ fontSize: 16 }} />
        ) : (
          <ErrorOutlineIcon sx={{ fontSize: 16 }} />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flex: 1,
          minWidth: 0,
          flexWrap: "wrap",
        }}
      >
        <Box component="span">
          <Box component="b" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
            Guest session
          </Box>{" "}
          — {daysLabel ?? defaultDays}
        </Box>
        <Box
          aria-hidden
          sx={{
            width: "4px",
            height: "4px",
            borderRadius: "50%",
            background: "currentColor",
            opacity: 0.4,
          }}
        />
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          {usedMB} MB of {totalMB} MB
          <Box
            aria-hidden
            sx={{
              width: 90,
              height: 6,
              borderRadius: "4px",
              background: "rgba(255,255,255,0.12)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ height: "100%", width: `${pct}%`, background: p.barFill, borderRadius: "4px" }} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ marginLeft: "auto" }}>
        <Button
          onClick={onSave}
          sx={{
            background: p.ctaBg,
            color: p.ctaText,
            fontFamily: FONT_FAMILY,
            fontSize: 13.5,
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "10px",
            padding: "8px 16px",
            "&:hover": { background: p.ctaHover, filter: state === "fresh" ? "none" : "brightness(1.06)" },
          }}
        >
          {saveLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default GuestBanner;
